import { useContext, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { DialogContent, MenuItem, Select, Stack, TextField, SelectChangeEvent, Checkbox, FormControlLabel } from '@mui/material';
import DropDownViewModel from '../../../Utilities/ViewModels/DropDownViewModel';
import { SessionContext } from '../../../Contexts/SessionContext';
import { useTripRateContext } from '../../TripRateContext';
import { getFilteredSurveys } from '../../../TripRateNew/TriprateFilters';
import { SurveyFullViewModel } from '../../../TripRateNew/ViewModels/SurveyFullViewModel';
//import ValidatedCheckBoxElement from '../../../Utilities/Forms/ValidatedCheckBoxElement';
import useData from '../../../Utilities/Hooks/UseData';
import { CounterBadge } from '../../../Utilities/CounterBadge';


const getFieldByDropdown = (survey: SurveyFullViewModel, dropdownValue: number) => {
    switch (dropdownValue) {
        case 1: return survey.ptpweekday0719;
        case 2: return survey.ptpweekday0710;
        case 3: return survey.ptpweekday1619;
        case 4: return survey.ptpsaturday0719;
        case 5: return survey.ptpsunday0719;
        default: return 0;
    }
};

export default function PublicTransportProvision() {
    const [open, setOpen] = useState(false);
    const { sessionState, setSessionField } = useContext(SessionContext);
    const { surveys } = useTripRateContext();

    const selected = sessionState?.primaryFilters?.publicTransportProvision ?? [];
    const { data: dropdown } = useData<DropDownViewModel[]>('TripRateFilterNew', 'GetSurveyRangesDropdown');

    const filteredSurveys = useMemo(() => {
        return getFilteredSurveys(
            surveys
        );
    }, [surveys, sessionState.primaryFilters.locationTypes, sessionState.primaryFilters.subLocationTypes,
        sessionState.secondaryFilters.useClasses, sessionState.secondaryFilters.travelPlan,
        sessionState.secondaryFilters.ptalRating, sessionState.secondaryFilters.petrolFillingStation,
        sessionState.residentialUnitTypes]);

    const range = useMemo(() => {
        if (!selected || selected === 0 || !filteredSurveys?.length) return { min: 0, max: 0 };

        const values = filteredSurveys
            .map(s => getFieldByDropdown(s, selected))
            .filter((v): v is number => typeof v === 'number' && !isNaN(v));

        if (!values.length) return { min: 0, max: 0 };

        const min = Math.min(...values);
        const max = Math.max(...values);

        setSessionField('primaryFilters.publicTransportMin', min);
        setSessionField('primaryFilters.publicTransportMax', max);

        return {
            min: min,
            max: max
        };
    }, [filteredSurveys, selected]);

    const ondropdownChange = (value: SelectChangeEvent<number>) => {
        setSessionField('primaryFilters.publicTransportProvision', value.target.value)
    }

    console.log(selected);
    console.log(dropdown);

    const includeNullRowsForPublicTransport =
        sessionState?.primaryFilters?.includeNullRowsForPublicTransport ?? true;

    return (
        <>
            <Grid container size={12} component={Paper} sx={{ position: 'relative' }}>
                <CounterBadge value={5} />

                <Grid size={12}>
                    <Typography textAlign="center">Public Transport Provision</Typography>
                </Grid>
                <Select
                    name={'publicTransportProvision'}
                    value={selected}
                    onChange={(value) => ondropdownChange(value)}>
                    {dropdown && dropdown.map((level, index) => (
                        <MenuItem key={level.id + index} value={level.id}>
                            {level.label}
                        </MenuItem>
                    ))}
                </Select>

                {selected !== 0 &&
                    <Grid container size={12}>
                        <Grid size={12}>
                            <FormControlLabel
                                label="Include days where PT not known"
                                control={
                                    <Checkbox
                                        name="includeNullRowsForPublicTransport"
                                        checked={includeNullRowsForPublicTransport}
                                        onChange={(event, checked) =>
                                            setSessionField(
                                                'primaryFilters.includeNullRowsForPublicTransport',
                                                checked
                                            )
                                        }
                                    />
                                }
                            />
                        </Grid>

                        <Grid size={4}>
                            <Stack spacing={3}>
                                <Typography variant={"body1"}>Minimum: {range?.min}</Typography>
                                <Typography variant={"body1"}>Maximum: {range?.max}</Typography>
                            </Stack>
                        </Grid>
                        <Grid size={8}>
                            <Stack spacing={1}>
                                <Grid size={12}>
                                    <TextField
                                        name="publicTransportMin"
                                        label="From:"
                                        type="number"
                                        value={sessionState?.primaryFilters?.publicTransportMin ?? ''}
                                        onChange={(e) =>
                                            setSessionField(
                                                'primaryFilters.publicTransportMin',
                                                e.target.value === '' ? null : Number(e.target.value)
                                            )
                                        }
                                    />

                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        name="publicTransportMax"
                                        label="To:"
                                        type="number"
                                        value={sessionState?.primaryFilters?.publicTransportMax ?? ''}
                                        onChange={(e) =>
                                            setSessionField(
                                                'primaryFilters.publicTransportMax',
                                                e.target.value === '' ? null : Number(e.target.value)
                                            )
                                        }
                                    />
                                </Grid>
                            </Stack>
                        </Grid>
                    </Grid>
                }
                <Button onClick={() => setOpen(true)}>Important Note</Button>
            </Grid>

            <Dialog onClose={() => setOpen(false)} open={open}>
                <DialogTitle>Important note on filtering by Public Transport</DialogTitle>
                <DialogContent>
                    Care should be taken when filtering survey days by public transport provision, as older data within TRICS does not have a Public Transport Provision Summary table present. The default option is to allow all surveys through this part of the filtering process, as stringent use of public transport filtering may lead to low levels of data within your selected set. However, users can select days of the week and peak time periods for filtering if necessary, and can allow the inclusion of survey days where the public transport provision is not known by leaving the "Include days where PT not known" box checked.
                </DialogContent>
            </Dialog>
        </>
    );
}
